import { authenticatedFetch } from './auth';

const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

export interface ChatMessage {
    role: 'user' | 'model';
    parts: { text: string }[];
}

export interface ToolCall {
    name: string;
    args: Record<string, unknown>;
    result: Record<string, unknown>;
}

export interface ChatShow {
    id: string;
    band?: string;
    band_name?: string;
    band_id?: string;
    band_photo_url?: string;
    band_soundcloud_url?: string;
    venue?: string;
    venue_name?: string;
    venue_id?: string;
    date?: string;
    time?: string;
    address?: string;
    description?: string;
}

export interface ChatBand {
    id: string;
    name: string;
    city?: string;
    state?: string;
    role?: string;
    genres?: string[];
    photo_url?: string;
    soundcloud_url?: string;
}

export interface ChatVenue {
    id: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    photo_url?: string;
}

export interface ChatMusician {
    id: string;
    name: string;
    first_name?: string;
    last_name?: string;
    instruments?: string[];
    genres?: string[];
    photo_url?: string;
    soundcloud_url?: string;
    bands?: { id: string; name: string }[];
}

export interface ChatPhoto {
    id: string;
    url: string;
    thumbnail_url?: string;
    show_id?: string;
    band_name?: string;
    venue_name?: string;
    type?: 'photo' | 'profile';
}

export interface ChatEntities {
    shows: ChatShow[];
    bands: ChatBand[];
    venues: ChatVenue[];
    musicians: ChatMusician[];
    photos: ChatPhoto[];
    primaryEntity?: 'shows' | 'bands' | 'venues' | 'musicians' | 'photos' | null;
}

export interface ChatResponse {
    text: string;
    toolCalls?: ToolCall[];
    entities?: ChatEntities;
}

/**
 * Check if a response has meaningful entities to display as cards
 */
export function hasDisplayableEntities(entities?: ChatEntities): boolean {
    if (!entities) return false;
    const primary = entities.primaryEntity;

    // Check based on primary entity type
    if (primary === 'shows') return entities.shows.length > 0;
    if (primary === 'bands') return entities.bands.length > 0;
    if (primary === 'venues') return entities.venues.length > 0;
    if (primary === 'musicians') return entities.musicians?.length > 0;
    if (primary === 'photos') return entities.photos?.length > 0;

    // Fallback: check all
    return entities.shows.length > 0 ||
           entities.bands.length > 0 ||
           entities.venues.length > 0 ||
           (entities.musicians?.length || 0) > 0 ||
           (entities.photos?.length || 0) > 0;
}

/**
 * Generate a brief summary based on tool calls and entities
 */
export function getEntitySummary(toolCalls?: ToolCall[], entities?: ChatEntities): string | null {
    if (!toolCalls || toolCalls.length === 0 || !entities) return null;

    const primary = entities.primaryEntity;

    for (const call of toolCalls) {
        switch (call.name) {
            case 'searchShows':
                // Use primaryEntity to determine the right summary
                if (primary === 'venues') {
                    if (entities.venues.length > 0) {
                        return `Found ${entities.venues.length} venue${entities.venues.length === 1 ? '' : 's'} with shows`;
                    }
                    return 'No venues found with shows matching your search';
                }
                if (primary === 'bands') {
                    if (entities.bands.length > 0) {
                        return `Found ${entities.bands.length} band${entities.bands.length === 1 ? '' : 's'} with shows`;
                    }
                    return 'No bands found with shows matching your search';
                }
                // Default to shows
                if (entities.shows.length > 0) {
                    return `Found ${entities.shows.length} show${entities.shows.length === 1 ? '' : 's'}`;
                }
                return 'No shows found matching your search';

            case 'searchBands':
                if (entities.bands.length > 0) {
                    return `Found ${entities.bands.length} band${entities.bands.length === 1 ? '' : 's'}`;
                }
                return 'No bands found matching your search';

            case 'searchVenues':
                if (entities.venues.length > 0) {
                    return `Found ${entities.venues.length} venue${entities.venues.length === 1 ? '' : 's'}`;
                }
                return 'No venues found matching your search';

            case 'searchMusicians':
                if (entities.musicians && entities.musicians.length > 0) {
                    return `Found ${entities.musicians.length} musician${entities.musicians.length === 1 ? '' : 's'}`;
                }
                return 'No musicians found matching your search';

            case 'listUserBands':
                if (entities.bands.length > 0) {
                    return `You manage ${entities.bands.length} band${entities.bands.length === 1 ? '' : 's'}`;
                }
                return "You don't manage any bands yet";

            case 'addShow':
                if (entities.shows.length > 0) {
                    const show = entities.shows[0];
                    return `Show created for ${show.band_name || show.band || 'the band'}!`;
                }
                break;

            case 'addBand':
                if (entities.bands.length > 0) {
                    return 'Band created successfully!';
                }
                break;

            case 'getBandPhotos':
            case 'getVenuePhotos':
                if (entities.photos && entities.photos.length > 0) {
                    return `Found ${entities.photos.length} photo${entities.photos.length === 1 ? '' : 's'}`;
                }
                return 'No photos found';

            case 'getLatestPhotos':
                if (entities.photos && entities.photos.length > 0) {
                    return `Here are the ${entities.photos.length} latest photo${entities.photos.length === 1 ? '' : 's'}`;
                }
                return 'No photos uploaded yet';

            case 'batchCreateShows':
                if (entities.shows.length > 0) {
                    return `Successfully created ${entities.shows.length} show${entities.shows.length === 1 ? '' : 's'}!`;
                }
                return 'Shows created';

            case 'parseShowListFromImage':
                // This is handled by the model text response
                return null;
        }
    }

    return null;
}

export interface ImageData {
    base64: string;
    mimeType: string;
}

export async function sendChatMessage(
    message: string,
    conversationHistory: ChatMessage[] = [],
    imageData?: ImageData
): Promise<ChatResponse> {
    const body: { message: string; conversationHistory: ChatMessage[]; imageData?: ImageData } = {
        message,
        conversationHistory
    };

    if (imageData) {
        body.imageData = imageData;
    }

    const response = await authenticatedFetch(`${baseUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Chat failed');
    }

    return response.json();
}

export interface TTSOptions {
    voiceName?: string;
    speakingRate?: number;
    pitch?: number;
}

export interface TTSResponse {
    audioContent: string; // Base64 encoded MP3
}

/**
 * Convert text to speech using Google Cloud TTS
 * @param text - The text to convert to speech
 * @param options - Voice configuration options
 * @returns Base64 encoded audio content
 */
export async function textToSpeech(
    text: string,
    options: TTSOptions = {}
): Promise<TTSResponse> {
    const response = await authenticatedFetch(`${baseUrl}/api/tts/synthesize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, ...options }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Text-to-speech failed');
    }

    return response.json();
}

/**
 * Play audio from base64 encoded content
 * @param audioContent - Base64 encoded audio
 * @returns Audio element for control
 */
export function playAudio(audioContent: string): HTMLAudioElement {
    const audio = new Audio(`data:audio/mp3;base64,${audioContent}`);
    audio.play();
    return audio;
}

export interface STTResponse {
    transcript: string;
}

/**
 * Convert speech to text using Google Cloud Speech-to-Text
 * @param audioContent - Base64 encoded audio content
 * @param mimeType - Audio MIME type (e.g., 'audio/webm')
 * @returns Transcribed text
 */
export async function speechToText(
    audioContent: string,
    mimeType: string = 'audio/webm'
): Promise<STTResponse> {
    const response = await authenticatedFetch(`${baseUrl}/api/tts/transcribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioContent, mimeType }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Speech-to-text failed');
    }

    return response.json();
}
